import { cn } from '../lib';
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { selectIsMinimalistic, useAppSelector } from '@/app/store';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva(
  'cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm leading-[20px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground active:bg-primary/90 hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm active:opacity-80 hover:opacity-80',
        outline: 'border border-solid border-input bg-background shadow-sm active:bg-accent hover:bg-accent',
        secondary:
          'bg-background-button border border-solid border-input text-primary active:bg-secondary active:border-transparent dark:border-none',
        ghost: 'text-primary active:bg-secondary',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'min-h-9 h-9 px-4 py-2',
        sm: 'min-h-8 h-8 px-3 text-xs leading-[16px]',
        lg: 'min-h-10 h-10 px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, isLoading = false, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const isMinimalistic = useAppSelector(selectIsMinimalistic);
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          isLoading && 'relative cursor-not-allowed opacity-75',
          isMinimalistic && 'rounded-none',
          isMinimalistic && (!variant || variant === 'default') && 'active:bg-primary/80 hover:bg-primary/80',
          isMinimalistic && variant === 'destructive' && 'shadow-none',
          isMinimalistic &&
            variant === 'outline' &&
            'shadow-none active:bg-background active:opacity-80 hover:bg-background hover:opacity-80',
          isMinimalistic &&
            variant === 'secondary' &&
            'active:opacity-80 active:bg-background active:border-input hover:opacity-80',
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <div className='flex gap-2 items-center justify-center'>
            <Loader2 className='animate-spin size-4' />
            {children}
          </div>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
